import React, { useMemo } from 'react';

import useQuery from '@/core/hooks/useQuery';
import { type CategoryMap, selectCategoryMap } from '@/core/lib/category';
import { QUERY_KEYS } from '@/core/lib/query/query.context';
import { useTranslationContext } from '@/core/lib/translation/translation.context';
import type { DonationsCategoriesResult } from '@/core/types/donation';
import { donationsCategoriesResultSchema } from '@/core/types/donation';

interface DonationCategoryProps {
  category: number | undefined;
}

const DonationCategory: React.FC<DonationCategoryProps> = ({ category }) => {
  const { t } = useTranslationContext(['common']);

  const { data } = useQuery<DonationsCategoriesResult, CategoryMap>({
    queryKey: QUERY_KEYS.categories,
    schema: donationsCategoriesResultSchema,
    select: selectCategoryMap,
  });

  const categoryToDisplay = useMemo(() => {
    if (!data) return null;

    const el = data.get(category ?? 0);

    if (!el) return null;

    const parent = data.get(el.parent ?? 0);
    const grandParent = data.get(parent?.parent ?? 0);

    return `${grandParent ? `${grandParent.label} > ` : ''}${parent ? `${parent.label} > ` : ''}${el.label}`;
  }, [data, category]);

  if (!category || !categoryToDisplay) {
    return null;
  }

  return (
    <section className="flex flex-col space-y-2 lg:space-y-1">
      <h2 className="text-body-primary font-semibold text-content-primary">{t('components.donation.details.category.title', { ns: 'common' })}</h2>
      <p className="text-body-secondary font-normal text-content-primary">{categoryToDisplay}</p>
    </section>
  );
};

export default DonationCategory;
